import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { NavLink, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Grid,
  Box,
  Tabs,
  Tab,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  Divider,
} from "@mui/material";
import { spacing } from "@mui/system";
import CustomerCardDashboard from "./CustomerCardDashboard";
import CustomerContacts from "./customercontacts";
import CustomerNotes from "./customernotes";
import CustomerLogins from "./customerLogins";
import CustomerInvoices from "./customerinvoices";
import CustomerClaims from "./customerClaims";
import CustomerExciseClaims from "./customerExciseClaims";
import CustomerFinancial from "./customerFinancial";
import CustomerDocuments from "./CustomerDocuments";
import CustomerPricingAgreements from "./CustomerPricingAgreements";
import CustomerSettings from "./CustomerSettings";
import axios from "axios";
import { getThemeTextColor } from "../../utils/themeUtils";
import { useTheme } from "@mui/material/styles";
import { usePermissions } from "../../hooks/usePermissions";
import { getVisibleCustomerTabs, resolveCustomerTabId } from "./customerOverviewTabs";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const CustomerDataGrid = () => {
  const theme = useTheme();
  const { hasPermission, permissions, permissionsLoaded } = usePermissions();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get("customerId");
  const customerNumber = searchParams.get("customerNumber");
  const tabParam = searchParams.get("tab");

  const [tableData, setTableData] = useState([]);
  const [activeTabId, setActiveTabId] = useState("card");

  const visibleTabs = useMemo(
    () => getVisibleCustomerTabs(hasPermission),
    [hasPermission, permissions, permissionsLoaded]
  );

  useEffect(() => {
    if (!customerId) return;
    axios
      .get("/api/v1/entities/" + customerId)
      .then((response) => {
        setTableData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching entities:", error);
      });
  }, [customerId]);

  useEffect(() => {
    setActiveTabId((currentTabId) => {
      if (tabParam) {
        return resolveCustomerTabId(tabParam, visibleTabs);
      }

      if (currentTabId && visibleTabs.some((tab) => tab.id === currentTabId)) {
        return currentTabId;
      }

      return resolveCustomerTabId(null, visibleTabs);
    });
  }, [tabParam, visibleTabs]);

  const handleTabChange = (_event, newTabId) => {
    setActiveTabId(newTabId);
  };

  const handleOpenContactsTab = () => {
    if (visibleTabs.some((tab) => tab.id === "contacts")) {
      setActiveTabId("contacts");
    }
  };

  const customerName = tableData?.name;

  return (
    <React.Fragment>
      <Helmet title="Invoices" />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            {customerName}
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/">
              Pages
            </Link>
            <Typography>Customer Overview</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Card mb={6}>
        <CardContent
          pb={1}
          sx={{
            marginBottom: 5,
          }}
        >
          <Typography variant="body2" gutterBottom>
            Click the tabs for different views.
          </Typography>

          <Box>
            <Box>
              <Tabs
                value={activeTabId || false}
                onChange={handleTabChange}
                variant="scrollable"
                allowScrollButtonsMobile
                aria-label="Customer overview tabs"
                sx={{
                  "& .MuiTab-root": {
                    ...getThemeTextColor(theme),
                  },
                  "& .Mui-selected": {
                    color: "primary.main",
                  },
                }}
              >
                {visibleTabs.map((tab) => (
                  <Tab
                    key={tab.id}
                    value={tab.id}
                    label={tab.label}
                    id={`customer-tab-${tab.id}`}
                    aria-controls={`customer-tabpanel-${tab.id}`}
                  />
                ))}
              </Tabs>
            </Box>
            <Box sx={{ padding: 2 }}>
              {activeTabId === "card" && (
                <Box id="customer-tabpanel-card" role="tabpanel" aria-labelledby="customer-tab-card">
                  <CustomerCardDashboard customerId={customerId} onOpenContactsTab={handleOpenContactsTab} />
                </Box>
              )}
              {activeTabId === "notes" && (
                <Box id="customer-tabpanel-notes" role="tabpanel" aria-labelledby="customer-tab-notes">
                  <CustomerNotes customerId={customerId} />
                </Box>
              )}
              {activeTabId === "contacts" && (
                <Box id="customer-tabpanel-contacts" role="tabpanel" aria-labelledby="customer-tab-contacts">
                  <CustomerContacts customerId={customerId} />
                </Box>
              )}
              {activeTabId === "logins" && (
                <Box id="customer-tabpanel-logins" role="tabpanel" aria-labelledby="customer-tab-logins">
                  <CustomerLogins customerId={customerId} />
                </Box>
              )}
              {activeTabId === "documents" && (
                <Box id="customer-tabpanel-documents" role="tabpanel" aria-labelledby="customer-tab-documents">
                  <CustomerDocuments customerId={customerId} />
                </Box>
              )}
              {activeTabId === "claims" && (
                <Box id="customer-tabpanel-claims" role="tabpanel" aria-labelledby="customer-tab-claims">
                  <CustomerClaims customerId={customerId} customerNumber={customerNumber} />
                </Box>
              )}
              {activeTabId === "invoices" && (
                <Box id="customer-tabpanel-invoices" role="tabpanel" aria-labelledby="customer-tab-invoices">
                  <CustomerInvoices customerId={customerId} customerNumber={customerNumber} />
                </Box>
              )}
              {activeTabId === "excise" && (
                <Box id="customer-tabpanel-excise" role="tabpanel" aria-labelledby="customer-tab-excise">
                  <CustomerExciseClaims customerId={customerId} customerNumber={customerNumber} />
                </Box>
              )}
              {activeTabId === "financial" && (
                <Box id="customer-tabpanel-financial" role="tabpanel" aria-labelledby="customer-tab-financial">
                  <CustomerFinancial customerId={customerId} customerNumber={customerNumber} />
                </Box>
              )}
              {activeTabId === "pricing" && (
                <Box id="customer-tabpanel-pricing" role="tabpanel" aria-labelledby="customer-tab-pricing">
                  <CustomerPricingAgreements customerId={customerId} />
                </Box>
              )}
              {activeTabId === "settings" && (
                <Box id="customer-tabpanel-settings" role="tabpanel" aria-labelledby="customer-tab-settings">
                  <CustomerSettings customerId={customerId} />
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </React.Fragment>
  );
};

export default CustomerDataGrid;
